import {DisplayObject} from 'pixi.js';
import {CoreDI, DeviceType, inject, injectable, IService, ScreenManager, SystemComponentType} from '@gongxigames/framework/core';
import {DI, GameConfig, SlotSettings} from '@gongxigames/framework/slot';
import {ScaleConfig, ScaleParameters, ScaleUtils} from '@gongxigames/framework/slot/utils/ScaleUtils';

@injectable()
export class ScaleService<ScaleConfigsMap = Record<string, ScaleConfig>> implements IService {
    public static NAME: symbol = DI.ScaleService;
    public type: SystemComponentType = 'service';

    @inject(CoreDI.GameConfig)
    protected readonly _gameConfig!: GameConfig;

    @inject(CoreDI.ScreenManager)
    protected readonly _screenManager!: ScreenManager;

    @inject(CoreDI.Settings)
    protected readonly _settings!: SlotSettings;

    protected _scaleUtils!: ScaleUtils;

    public init(): void {
        const configs = this._gameConfig.scaleConfigsByName;

        if (configs) {
            this._scaleUtils = new ScaleUtils(
                configs,
                this._screenManager.mobilePortraitModeScale,
                this._gameConfig.viewport.mobile.width
            );
        }
    }

    public applyScale<K extends keyof ScaleConfigsMap>(configName: K, ...displayObjects: DisplayObject[]): void {
        const platform = this._settings.get('platform');

        this._scaleUtils.scaleByTargetPlatform(configName as string, platform, this._screenManager.isLandscapeOrientation(), ...displayObjects);
    }

    public applyDesktopScale<K extends keyof ScaleConfigsMap>(configName: K, ...displayObjects: DisplayObject[]): void {
        this._scaleUtils.scaleByTargetPlatform(configName as string, DeviceType.DESKTOP, true, ...displayObjects);
    }

    public applyMobileScale<K extends keyof ScaleConfigsMap>(configName: K, ...displayObjects: DisplayObject[]): void {
        this._scaleUtils.scaleByTargetPlatform(configName as string, DeviceType.MOBILE, this._screenManager.isLandscapeOrientation(), ...displayObjects);
    }

    public applyPortraitMobileScale<K extends keyof ScaleConfigsMap>(configName: K, ...displayObjects: DisplayObject[]): void {
        this._scaleUtils.scaleByTargetPlatform(configName as string, DeviceType.MOBILE, false, ...displayObjects);
    }

    public applyLandscapeMobileScale<K extends keyof ScaleConfigsMap>(configName: K, ...displayObjects: DisplayObject[]): void {
        this._scaleUtils.scaleByTargetPlatform(configName as string, DeviceType.MOBILE, true, ...displayObjects);
    }

    public getMobileReelsParameters<K extends keyof ScaleConfigsMap>(configName: K): ScaleParameters {
        const params = this._scaleUtils.getAllScaleParameters(configName as string);

        return this._screenManager.isLandscapeOrientation() ? params.landscape : params.portrait;
    }

    public get scale(): ScaleUtils {
        return this._scaleUtils;
    }
}
